import type { JSX, ReactNode } from "react";
import { useRef, useState } from "react";

import { resolveCityPageGlyph } from "@noko-city/shared";

import { Reveal } from "./Reveal";

import type { CityPageSectionImage } from "@/lib/api";

/**
 * One guide section: the homepage's kicker grammar (mono, orange hairline)
 * introducing the section name set large in Anton, then a drawn accent rule and
 * the body.
 *
 * A numbered `index` turns the eyebrow into `01 / A LITTLE HISTORY`. That is the
 * homepage's own pattern and its reason: there the kicker never repeats the
 * display line (`02 / CLOSER TO HOME` over `MIDWEEK.`), and an eyebrow carrying
 * the heading's own words is a second copy of the heading rather than a label.
 * Without an `index` the eyebrow keeps the heading words, so any caller that has
 * not opted in renders exactly as it did before.
 *
 * The rule is the reveal vocabulary's own `draw` variant — the site's motion
 * language is "hairlines draw left-to-right, rows stamp in" — and it takes the
 * section's tone so each stop carries its own colour.
 */
export function GuideSection({
  heading,
  icon,
  index,
  id,
  children,
  delay = 0,
  tone,
}: {
  heading: string;
  /**
   * The section's own heading glyph, printed before its words. A custom-emoji
   * shortcode the author picked arrives as an image URL (the emoji lives on our
   * CDN, which is where the landing's own images come from) and anything else is
   * the character itself.
   */
  icon?: string;
  /** 1-based position among the guide's rendered sections; numbers the eyebrow. */
  index?: number;
  /** Anchor target for the contents strip. */
  id?: string;
  children: ReactNode;
  delay?: number;
  tone?: "amber" | "magenta";
}): JSX.Element {
  return (
    <Reveal variant="rise" delay={delay}>
      <section id={id} className="atlas-guide-col atlas-guide-section" data-tone={tone}>
        {index !== undefined && (
          <span className="atlas-guide-ghost" aria-hidden="true">
            {String(index).padStart(2, "0")}
          </span>
        )}
        <p className="atlas-kicker" aria-hidden="true">
          {index === undefined ? heading : `${String(index).padStart(2, "0")} / ${heading}`}
        </p>
        <h2 className="atlas-guide-heading">
          <HeadingGlyph icon={icon} />
          {heading}
        </h2>
        <Reveal variant="draw" className="atlas-guide-rule" />
        <div className="atlas-guide-body">{children}</div>
      </section>
    </Reveal>
  );
}

/**
 * A section's heading glyph: an emoji the author picked, or one of our own images
 * when the value is a URL we serve. Nothing when the section has none — which is
 * what keeps a guide without glyphs byte-identical to the one it rendered before
 * this existed — and nothing for an image we do not serve, because a public page
 * never hotlinks someone else's file.
 *
 * Decorative by definition (the heading beside it says what the section is), so it
 * is hidden from assistive tech rather than read out as a second label.
 */
export function HeadingGlyph({ icon }: { icon: string | undefined }): JSX.Element | null {
  const glyph = resolveCityPageGlyph(icon);
  if (!glyph) return null;
  return (
    <span className="atlas-guide-heading-glyph" aria-hidden="true">
      {glyph.kind === "image" ? (
        <img src={glyph.url} alt="" className="atlas-guide-heading-glyph-image" />
      ) : (
        glyph.text
      )}
    </span>
  );
}

/**
 * A section illustration: the bytes the API resolved for this city — never a
 * placeholder, never another city's picture, text-only when the slot is empty.
 * No caption: provenance lives in the media record, not in the DOM.
 *
 * The published guides are all 1264×848 landscape, so the box is fixed at 3:2
 * and the image covers it: the section cannot shift as the lazy image decodes,
 * and every slot reads at the same weight regardless of the picture's own crop.
 *
 * That reserved box is also why a picture that never arrives has to be handled:
 * a slot that fails leaves the 3:2 hole standing above the prose, which reads as
 * a broken section rather than a missing picture. A failure instead marks the row
 * the same way an absent slot is marked — it takes the whole width — and drops
 * the figure, so the text closes up. With scripting off there is nothing to run
 * that handler, which is the one case where the hole can still appear.
 */
export function GuideFigure({ image }: { image: CityPageSectionImage }): JSX.Element | null {
  const [failed, setFailed] = useState(false);
  const ref = useRef<HTMLElement>(null);
  if (failed) return null;
  return (
    <figure ref={ref} className="m-0 w-full">
      <img
        src={image.src}
        alt={image.alt}
        width={image.width}
        height={image.height}
        loading="lazy"
        decoding="async"
        onError={() => {
          ref.current?.closest(".atlas-guide-row")?.classList.add("atlas-guide-row--text-only");
          setFailed(true);
        }}
        className="block aspect-[3/2] w-full border border-white/15 object-cover"
      />
    </figure>
  );
}
